import React from "react";
import {
    AlertTriangle,
    FileText,
    Settings,
    CheckCircle,
    UserPlus,
    Play,
    Users,
    Shield,
    ClipboardCheck,
    Wrench,
    Package,
    DollarSign,
    Warehouse,
    Search
} from "lucide-react";
// import { FileCodeIcon } from "@radix-ui/react-icons";

export const guides = [
    {
        id: "mro-report-fault",
        title: "MRO/Technician: Report a Fault",
        icon: <AlertTriangle size={20} />,
        difficulty: "Easy",
        category: "Fault Reporting",
        steps: [
            {
                title: "Access Fault Reporting",
                description:
                    "Navigate to Operations > Faults > 'Fault Portal' to access the fault reporting interface where you can report issues on assets you have access to.",
                image: "/media/help/fault-portal-form.png"
            },
            {
                title: "Select Asset Information",
                description:
                    "Search for the affected asset or site in the single combined search box - start typing a name or ID and pick it from the results (assets and sites/clusters both appear together). You can select more than one target if the fault affects several assets at once.",
                image: "/media/help/fault-portal-form.png"
            },
            {
                title: "Specify Fault Details",
                description:
                    "Select the Fault Type from the dropdown (Repair, Replacement, etc.) and provide a detailed description of the issue, including symptoms, severity, and any immediate safety concerns.",
                image: "/media/help/fault-portal-form.png"
            },
            {
                title: "Upload Supporting Evidence",
                description:
                    "Drop files or click to upload images, documents, or other evidence that supports your fault report. Visual evidence helps Asset Owners understand the issue better.",
                image: "/media/help/fault-portal-form.png"
            },
            {
                title: "Submit Fault Report",
                description:
                    "Review all information for accuracy and click 'Next' to submit the fault report. If you're staff, it goes to your MRO for approval first. If you're an MRO, it goes directly to the Asset Owner.",
                image: "/media/help/fault-portal-form.png"
            }
        ]
    },
    {
        id: "fault-portal-access",
        title: "Access Asset Owner Fault Portal",
        icon: <AlertTriangle size={20} />,
        difficulty: "Easy",
        category: "Fault Reporting",
        steps: [
            {
                title: "Navigate to Fault Portal URL",
                description:
                    "Go to Operations > Faults > 'Fault Portal' from the navigation menu to access the fault portal URL generator for specific Asset Owners.",
                image: "/media/help/fault-portal-url.png"
            },
            {
                title: "Select Asset Owner",
                description:
                    "Choose the Asset Owner from the dropdown list. This will show Asset Owners where your organization has established connections and access rights.",
                image: "/media/help/fault-portal-url.png"
            },
            {
                title: "Generate and Copy Portal Link",
                description:
                    "The system generates a unique URL for the selected Asset Owner's fault portal. Click 'Copy Link' to copy the URL for easy access or sharing with team members.",
                image: "/media/help/fault-portal-url.png"
            },
            {
                title: "Access Direct Fault Reporting",
                description:
                    "Use the copied URL to directly access the fault reporting form for that specific Asset Owner, streamlining the fault reporting process for their assets.",
                image: "/media/help/fault-portal-url.png"
            }
        ]
    },
    {
        id: "mro-manage-fault-approvals",
        title: "MRO: Manage Staff Fault Approvals",
        icon: <CheckCircle size={20} />,
        difficulty: "Medium",
        category: "Fault Reporting",
        steps: [
            {
                title: "Access Approval Requests",
                description:
                    "Navigate to 'Reported Faults' and click the 'Approval Requests' tab to view fault reports submitted by your staff members that require your approval before being sent to Asset Owners.",
                image: "/media/help/reported-faults-dashboard.png"
            },
            {
                title: "Review Staff Fault Reports",
                description:
                    "Examine each fault report including Asset, Location, Fault Type, Reporter details, and Status. Click on any fault to view comprehensive details and supporting evidence.",
                image: "/media/help/reported-faults-dashboard.png"
            },
            {
                title: "Evaluate Fault Report Quality",
                description:
                    "Review the fault details including Reporter information, Fault Type, Description, uploaded documents, and Reported Date to ensure the report meets quality standards.",
                image: "/media/help/fault-details-actions.png"
            },
            {
                title: "Approve or Reject Fault Reports",
                description:
                    "Use the 'Actions' dropdown to either approve the fault report (sending it to the Asset Owner) or reject it (returning it to staff for revision or correction).",
                image: "/media/help/fault-details-actions.png"
            },
            {
                title: "Track Approval Status",
                description:
                    "Monitor the status of fault reports through the approval workflow and ensure timely processing to maintain good relationships with Asset Owners.",
                image: "/media/help/reported-faults-dashboard.png"
            }
        ]
    },
    {
        id: "asset-owner-review-faults",
        title: "Asset Owner: Review and Act on Fault Reports",
        icon: <Settings size={20} />,
        difficulty: "Medium",
        category: "Fault Reporting",
        steps: [
            {
                title: "Access Reported Faults",
                description:
                    "Navigate to 'Reported Faults' to view all fault reports submitted by MRO organizations and technicians for your assets. Use the 'Open Faults' tab to see pending reports.",
                image: "/media/help/reported-faults-dashboard.png"
            },
            {
                title: "Review Fault Report Details",
                description:
                    "Click on any fault report to view comprehensive details including Reporter information, Fault Type, detailed description, uploaded evidence, and reported date.",
                image: "/media/help/asset-owner-fault-details.png"
            },
            {
                title: "Evaluate Fault Validity",
                description:
                    "Assess the fault report based on the description, supporting evidence, and your knowledge of the asset condition to determine if the reported fault is valid and requires action.",
                image: "/media/help/asset-owner-fault-details.png"
            },
            {
                title: "Take Action on Fault Reports",
                description:
                    "Use the Actions dropdown to: 'Reject' if the fault is invalid, 'Raise SR' to create a service request for the fault, or 'Raise WO' to create a work order directly from the fault report.",
                image: "/media/help/asset-owner-fault-details.png"
            },
            {
                title: "Track Fault Resolution",
                description:
                    "Monitor fault reports through to resolution and use the 'Closed Faults' tab to review completed fault reports and the actions taken to address them.",
                image: "/media/help/reported-faults-dashboard.png"
            }
        ]
    },
    {
        id: "manage-fault-status",
        title: "Manage Fault Report Status and Tracking",
        icon: <FileText size={20} />,
        difficulty: "Easy",
        category: "Fault Reporting",
        steps: [
            {
                title: "Understanding Fault Status Tabs",
                description:
                    "The Reported Faults interface has four key tabs: 'Open Faults' (pending review), 'Closed Faults' (resolved), 'Approval Requests' (awaiting MRO approval), and 'My Pending Requests' (your submissions awaiting action).",
                image: "/media/help/reported-faults-dashboard.png"
            },
            {
                title: "Track Open Faults",
                description:
                    "Use the 'Open Faults' tab to monitor fault reports that are still pending review or action. These require attention from either MRO approvers or Asset Owners depending on the workflow stage.",
                image: "/media/help/reported-faults-dashboard.png"
            },
            {
                title: "Review Closed Faults",
                description:
                    "The 'Closed Faults' tab shows all resolved fault reports, including those that were rejected, had work orders created, or were otherwise resolved. Use this for historical reference and trend analysis.",
                image: "/media/help/reported-faults-dashboard.png"
            },
            {
                title: "Monitor Your Pending Requests",
                description:
                    "Use 'My Pending Requests' to track fault reports you've submitted that are awaiting approval from your MRO organization or review from Asset Owners.",
                image: "/media/help/reported-faults-dashboard.png"
            },
            {
                title: "Search and Filter Faults",
                description:
                    "Use the search functionality to find specific fault reports by Asset, Location, Fault Type, Reporter, or Status. This helps manage large volumes of fault reports efficiently.",
                image: "/media/help/reported-faults-dashboard.png"
            }
        ]
    },
    {
        id: "fault-reporting-workflow",
        title: "Understanding Fault Reporting Workflow",
        icon: <Users size={20} />,
        difficulty: "Easy",
        category: "Fault Reporting",
        steps: [
            {
                title: "MRO/Technician Fault Submission",
                description:
                    "MRO organizations and technicians can report faults on assets they have access to through established connections with Asset Owners. They use the Fault Portal to submit detailed fault reports with supporting evidence.",
                image: "/media/help/fault-navigation-menu.png"
            },
            {
                title: "Staff Approval Workflow",
                description:
                    "When staff members of an MRO organization submit fault reports, these first go to their MRO organization for approval before being sent to the Asset Owner. This ensures quality control and organizational oversight.",
                image: "/media/help/fault-details-actions.png"
            },
            {
                title: "Direct MRO Submission",
                description:
                    "When MRO organizations (not staff) submit fault reports directly, these go straight to the Asset Owner for review, bypassing the internal approval step since they have organizational authority.",
                image: "/media/help/fault-portal-form.png"
            },
            {
                title: "Asset Owner Review and Action",
                description:
                    "Asset Owners receive fault reports and can review them, then either reject invalid reports, raise service requests for investigation, or create work orders directly to address the reported faults.",
                image: "/media/help/asset-owner-fault-details.png"
            },
            {
                title: "Fault Resolution Tracking",
                description:
                    "All parties can track fault reports through their lifecycle using status tabs: Open (pending), Closed (resolved), Approval Requests (awaiting MRO approval), and My Pending Requests (personal submissions).",
                image: "/media/help/reported-faults-dashboard.png"
            }
        ]
    },
    {
        id: "asset-owner-create-service-request",
        title: "Asset Owner: Create Service Request",
        icon: <FileText size={20} />,
        difficulty: "Easy",
        category: "Service Request",
        steps: [
            {
                title: "Access Service Request Creation",
                description:
                    "Navigate to 'Service Request' and click 'Send Service Request' to create a new service request specifying operations to be performed on your assets.",
                image: "/media/help/create-service-request-form.png"
            },
            {
                title: "Select Service Request Type and Operations",
                description:
                    "Choose the SR Type from the dropdown (Instant, Scheduled, etc.) and provide a detailed description of the specific operations you need performed on your assets.",
                image: "/media/help/create-service-request-form.png"
            },
            {
                title: "Choose Asset or Cluster Scope",
                description:
                    "Decide whether the requested operations should be performed on the whole cluster (Yes) or individual asset (No). This determines the scope of operations required.",
                image: "/media/help/create-service-request-form.png"
            },
            {
                title: "Select Asset/Cluster and Location",
                description:
                    "Choose the specific Cluster Name or Asset Name where operations need to be performed, select the Location, and specify which assets require the requested operations.",
                image: "/media/help/create-service-request-form.png"
            },
            {
                title: "Assign Receiver Organization",
                description:
                    "Select the receiver from IMO, MRO, or EXT. TECHNICIAN options, then choose the specific organization that will perform the requested operations.",
                image: "/media/help/create-service-request-form.png"
            },
            {
                title: "Submit Service Request",
                description:
                    "Review all operation details and click 'Submit' to send the service request to the selected MRO organization for execution.",
                image: "/media/help/create-service-request-form.png"
            }
        ]
    },
    {
        id: "asset-owner-manage-outgoing-requests",
        title: "Asset Owner: Manage Outgoing Requests",
        icon: <FileText size={20} />,
        difficulty: "Medium",
        category: "Service Request",
        steps: [
            {
                title: "View Outgoing Service Requests",
                description:
                    "Navigate to 'Service Request' > 'Outgoing' to see all operations you've requested from MRO organizations, including SR ID, type, date sent, and SLA information.",
                image: "/media/help/asset-owner-outgoing-sr-details.png"
            },
            {
                title: "Review Service Request Details",
                description:
                    "Click on any service request to view comprehensive details including the specific operations requested, SLA penalty status, and associated asset information.",
                image: "/media/help/asset-owner-outgoing-sr-details.png"
            },
            {
                title: "Monitor SLA Compliance",
                description:
                    "Check the SLA penalty status and click 'View SLA' to review the service level agreement terms that bind the service provider to complete the requested operations.",
                image: "/media/help/asset-owner-outgoing-sr-details.png"
            },
            {
                title: "View Submitted Observations",
                description:
                    "Click 'View Observations' to see all findings and fault reports submitted by the MRO organization during their execution of the requested operations.",
                image: "/media/help/asset-owner-outgoing-sr-details.png"
            },
            {
                title: "Close Service Request",
                description:
                    "Once satisfied with the operations performed and observations received about any discovered faults, use 'Close SR' action to formally close the service request.",
                image: "/media/help/asset-owner-outgoing-sr-details.png"
            }
        ]
    },
    {
        id: "mro-handle-incoming-requests",
        title: "MRO: Handle Incoming Service Requests",
        icon: <CheckCircle size={20} />,
        difficulty: "Medium",
        category: "Service Request",
        steps: [
            {
                title: "View Incoming Service Requests",
                description:
                    "Navigate to 'Service Request' > 'Incoming' to see all operations requested by Asset Owners that your organization needs to perform.",
                image: "/media/help/mro-incoming-service-requests.png"
            },
            {
                title: "Review Service Request Details",
                description:
                    "Click on any incoming service request to view full details including the specific operations to be performed, asset information, and SLA requirements.",
                image: "/media/help/mro-incoming-sr-actions.png"
            },
            {
                title: "Assign to Staff Members",
                description:
                    "Use 'Assign to staff' action to delegate the requested operations to appropriate technicians within your organization based on skills and availability.",
                image: "/media/help/mro-incoming-sr-actions.png"
            },
            {
                title: "Report Observations Directly",
                description:
                    "Click 'Report Observation' to submit findings about discovered faults and requirements needed to fix them directly to the Asset Owner during or after performing operations.",
                image: "/media/help/mro-incoming-sr-actions.png"
            },
            {
                title: "View Existing Observations",
                description:
                    "Use 'View Observation' to see all fault findings and repair requirements that have been reported by your organization or staff for this service request.",
                image: "/media/help/mro-incoming-sr-actions.png"
            }
        ]
    },
    {
        id: "mro-submit-observations",
        title: "MRO: Submit Service Request Observations",
        icon: <FileText size={20} />,
        difficulty: "Medium",
        category: "Service Request",
        steps: [
            {
                title: "Access Observation Reporting",
                description:
                    "From an incoming service request, click 'Report Observation' or navigate to 'Service Request' > 'Observation' to submit detailed findings about discovered faults and repair requirements.",
                image: "/media/help/report-observation-form.png"
            },
            {
                title: "Select Observation Type",
                description:
                    "Choose the appropriate Observation type from the dropdown (Okay, Not Okay, etc.) to categorize the findings and fault conditions discovered during operations.",
                image: "/media/help/report-observation-form.png"
            },
            {
                title: "Provide Report Summary of Findings",
                description:
                    "Enter a detailed Report Summary describing the faults discovered, asset conditions found, and any issues that require attention or repair.",
                image: "/media/help/report-observation-form.png"
            },
            {
                title: "Set Completion Time and Upload Evidence",
                description:
                    "Specify the 'Time to complete' repairs and upload pictures or documents that support your findings and demonstrate the discovered faults or conditions.",
                image: "/media/help/report-observation-form.png"
            },
            {
                title: "Add Service and Spare Parts Requirements",
                description:
                    "Use 'Add observation' to itemize services required and spare parts needed to fix the discovered faults, including quantities, estimated costs, and repair specifications.",
                image: "/media/help/report-observation-form.png"
            },
            {
                title: "Submit Observation to Asset Owner",
                description:
                    "Review all findings and repair requirements for accuracy and click 'Submit Observation' to send the detailed fault report to the Asset Owner for review and action.",
                image: "/media/help/report-observation-form.png"
            }
        ]
    },
    {
        id: "mro-manage-staff-observations",
        title: "MRO: Manage Staff Observations",
        icon: <Users size={20} />,
        difficulty: "Medium",
        category: "Service Request",
        steps: [
            {
                title: "Review Staff Submitted Observations",
                description:
                    "Navigate to 'Service Request' > 'Observation' to view all fault findings and repair requirements submitted by your staff members for various service requests.",
                image: "/media/help/mro-observation-details.png"
            },
            {
                title: "Evaluate Observation Quality",
                description:
                    "Review the Observation Type, Report Summary of discovered faults, completion time estimates, and uploaded evidence to ensure quality and completeness of fault reports.",
                image: "/media/help/mro-observation-details.png"
            },
            {
                title: "Review Cost Breakdown for Repairs",
                description:
                    "Examine the detailed breakdown of services required and spare parts needed to fix discovered faults, including quantities, charges, and totals before forwarding to Asset Owner.",
                image: "/media/help/mro-observation-details.png"
            },
            {
                title: "Take Action on Observations",
                description:
                    "Use available actions: 'Rate Service Provider' for performance evaluation, 'Accept' to approve and forward fault reports, 'Reject' to return for revision, or 'Raise WO' to create repair work orders.",
                image: "/media/help/mro-observation-details.png"
            },
            {
                title: "Amend and Forward Observations",
                description:
                    "If needed, amend staff observations by adding additional fault information or repair requirements before forwarding to the Asset Owner to ensure comprehensive fault reporting.",
                image: "/media/help/mro-observation-details.png"
            }
        ]
    },
    {
        id: "view-sent-observations",
        title: "View All Sent Observations",
        icon: <CheckCircle size={20} />,
        difficulty: "Easy",
        category: "Service Request",
        steps: [
            {
                title: "Access Sent Observations",
                description:
                    "Navigate to 'Service Request' > 'Observation' > 'Sent' to view all fault findings and repair requirements that have been submitted to Asset Owners for service requests.",
                image: "/media/help/sent-observation-details.png"
            },
            {
                title: "Review Observation History",
                description:
                    "View the complete history of sent observations including Observation Type, fault findings summary, and completion dates for tracking and reference purposes.",
                image: "/media/help/sent-observation-details.png"
            },
            {
                title: "Track Service and Cost Requirements",
                description:
                    "Review the detailed breakdown of services required and spare parts needed to fix discovered faults, including all costs and charges that were reported to Asset Owners.",
                image: "/media/help/sent-observation-details.png"
            },
            {
                title: "Monitor Observation Status",
                description:
                    "Track which fault reports have been sent and are awaiting Asset Owner response or action, helping manage follow-up communications about repair requirements.",
                image: "/media/help/sent-observation-details.png"
            }
        ]
    },
    {
        id: "asset-owner-review-observations",
        title: "Asset Owner: Review and Act on Observations",
        icon: <Settings size={20} />,
        difficulty: "Medium",
        category: "Service Request",
        steps: [
            {
                title: "Access Submitted Observations",
                description:
                    "From your outgoing service requests, click 'View Observations' to see all fault findings and repair requirements submitted by MRO organizations during their operations.",
                image: "/media/help/asset-owner-outgoing-sr-details.png"
            },
            {
                title: "Review Observation Details",
                description:
                    "Examine the observation type, fault findings summary, repair completion time estimates, and any uploaded evidence of discovered faults provided by the service provider.",
                image: "/media/help/mro-observation-details.png"
            },
            {
                title: "Analyze Cost Breakdown for Repairs",
                description:
                    "Review the detailed breakdown of services required and spare parts needed to fix discovered faults, including quantities, unit charges, and total repair costs for budget planning.",
                image: "/media/help/mro-observation-details.png"
            },
            {
                title: "Take Action on Fault Reports",
                description:
                    "Based on the fault findings and repair requirements, you can approve the recommended repairs, request additional information, or raise follow-up service requests for the repair work.",
                image: "/media/help/asset-owner-outgoing-sr-details.png"
            },
            {
                title: "Close Service Request",
                description:
                    "Once satisfied with all fault findings and repair requirements submitted, use the 'Close SR' action to formally close the service request and plan for necessary repairs.",
                image: "/media/help/asset-owner-outgoing-sr-details.png"
            }
        ]
    },
    {
        id: "completed-service-requests",
        title: "Manage Completed Service Requests",
        icon: <CheckCircle size={20} />,
        difficulty: "Easy",
        category: "Service Request",
        steps: [
            {
                title: "Access Completed Requests",
                description:
                    "Navigate to 'Service Request' > 'Completed' to view all operations that have been completed and closed by both Asset Owners and MRO organizations.",
                image: "/media/help/completed-service-request.png"
            },
            {
                title: "Review Completed Request Details",
                description:
                    "Click on any completed service request to view the final details of operations performed, including closure status and all associated asset information.",
                image: "/media/help/completed-service-request.png"
            },
            {
                title: "Access Final Observations",
                description:
                    "Use 'View Observations' to review all final fault findings and repair requirements that were submitted during the service request lifecycle for record keeping.",
                image: "/media/help/completed-service-request.png"
            },
            {
                title: "Archive and Reference",
                description:
                    "Use completed service requests as reference for future similar operations, fault patterns, repair cost estimation, and performance evaluation of service providers.",
                image: "/media/help/completed-service-request.png"
            }
        ]
    },
    {
        id: "role-based-service-requests",
        title: "Understanding Role-Based Service Requests",
        icon: <Users size={20} />,
        difficulty: "Easy",
        category: "Service Request",
        steps: [
            {
                title: "Asset Owner Service Request Flow",
                description:
                    "Asset Owners create and send service requests (operations to be performed) to MRO organizations. They can view 'Outgoing' requests (operations sent to service providers) and 'Completed' requests (finished operations with fault reports).",
                image: "/media/help/service-request-owner-menu.png"
            },
            {
                title: "MRO/Technician Service Request Flow",
                description:
                    "MRO organizations and technicians receive service requests (operations to perform) from Asset Owners. They see 'Incoming' requests (new operations to perform), 'Completed' requests (finished operations), and 'Observations' (fault reports they've submitted).",
                image: "/media/help/service-request-mro-menu.png"
            },
            {
                title: "Request Direction Flow",
                description:
                    "Service requests (operations) flow from Asset Owners (who request 'Outgoing' operations) to MRO/Technicians (who receive 'Incoming' operations to perform). This creates a clear operational workflow hierarchy.",
                image: ""
            },
            {
                title: "Observation Workflow",
                description:
                    "Only MRO organizations and technicians can submit observations (fault findings and repair requirements) back to Asset Owners. These observations provide detailed reports on faults discovered during operations and requirements needed to fix them.",
                image: ""
            },
            {
                title: "Completion Tracking",
                description:
                    "Both Asset Owners and MRO/Technicians can view 'Completed' requests, but from different perspectives - Asset Owners see operations they requested, while MROs see operations they performed and fault findings they reported.",
                image: ""
            }
        ]
    },
    {
        id: "asset-owner-create-work-order",
        title: "Asset Owner: Create Work Order",
        icon: <Wrench size={20} />,
        difficulty: "Medium",
        category: "Work Order",
        steps: [
            {
                title: "Navigate to Work Order Creation",
                description:
                    "From the Work Order menu, click 'Outgoing' and then 'Raise New WO' to create a new work order for asset maintenance or repairs.",
                image: "/media/help/create-work-order-form.png"
            },
            {
                title: "Set Work Order Type and Description",
                description:
                    "Select the Work Order Type (Instant, Scheduled, etc.) and provide a detailed description of the work to be performed on your assets.",
                image: "/media/help/create-work-order-form.png"
            },
            {
                title: "Choose Asset or Cluster Scope",
                description:
                    "Decide whether to send the work order for the whole cluster (Yes) or individual asset (No). This determines the scope of work required.",
                image: "/media/help/create-work-order-form.png"
            },
            {
                title: "Select Asset/Cluster and Location",
                description:
                    "Choose the specific Cluster Name or Asset Name, select the Location, and specify which assets need the work performed.",
                image: "/media/help/create-work-order-form.png"
            },
            {
                title: "Assign Receiver Organization",
                description:
                    "Select the receiver from IMO, MRO, or EXT. TECHNICIAN options. If no responsible service provider is assigned to the asset/cluster, you have flexibility to assign to anyone.",
                image: "/media/help/create-work-order-form.png"
            },
            {
                title: "Submit Work Order",
                description:
                    "Review all details and click 'Submit'. The work order will check for approval configuration and wait for required approvals before being sent to the responsible MRO.",
                image: "/media/help/create-work-order-form.png"
            }
        ]
    },
    {
        id: "asset-owner-configure-approvals",
        title: "Asset Owner: Configure Work Order Approvals",
        icon: <ClipboardCheck size={20} />,
        difficulty: "Medium",
        category: "Work Order",
        steps: [
            {
                title: "Access Work Order Configuration",
                description:
                    "Navigate to Administration > Approval Workflows > 'Work Order Approvals' to set up approval levels and notification settings for your organization's work order management.",
                image: "/media/help/work-order-configuration.png"
            },
            {
                title: "Create Approval Levels",
                description:
                    "Click 'Create new level' to add approval categories (Head O&M, GM, CEO, etc.) with specific maximum amounts and assigned users who can approve work orders up to those amounts.",
                image: "/media/help/add-approval-level-modal.png"
            },
            {
                title: "Set Maximum Amounts",
                description:
                    "Define the maximum amount (NGN) that each approval category can authorize. Higher amounts require higher-level approvals (e.g., CEO for 10,000,000 NGN).",
                image: "/media/help/work-order-configuration.png"
            },
            {
                title: "Assign Approval Users",
                description:
                    "For each approval level, assign specific users from your organization who have the authority to approve work orders within that category's limit.",
                image: "/media/help/add-approval-level-modal.png"
            },
            {
                title: "Configure Notifications",
                description:
                    "Set up Work Order Notification Configuration to specify which users receive email and notification alerts when work orders are created, approved, or completed.",
                image: "/media/help/work-order-configuration.png"
            }
        ]
    },
    {
        id: "asset-owner-manage-approval-requests",
        title: "Asset Owner: Manage Approval Requests",
        icon: <CheckCircle size={20} />,
        difficulty: "Easy",
        category: "Work Order",
        steps: [
            {
                title: "Access Approval Requests",
                description:
                    "Navigate to 'Work Order' > 'Approval Requests' to view all work orders awaiting approval within your organization.",
                image: "/media/help/work-order-approval-requests.png"
            },
            {
                title: "Review Work Order Details",
                description:
                    "Check the WO ID, Status (Approved by me, Awaiting Approval), Cost, Expected Completion Date, and Asset information for each pending work order.",
                image: "/media/help/work-order-approval-requests.png"
            },
            {
                title: "Approve or Reject Work Orders",
                description:
                    "Use the Actions column to approve work orders within your authorization limit or reject them if they don't meet requirements. Approved work orders move to the next approval level or get sent to MRO.",
                image: "/media/help/work-order-approval-requests.png"
            },
            {
                title: "Track Approval Status",
                description:
                    "Monitor which work orders you've approved and which are still awaiting approval from other authorized personnel in the approval chain.",
                image: "/media/help/work-order-approval-requests.png"
            }
        ]
    },
    {
        id: "asset-owner-manage-outgoing-work-orders",
        title: "Asset Owner: Manage Outgoing Work Orders",
        icon: <FileText size={20} />,
        difficulty: "Medium",
        category: "Work Order",
        steps: [
            {
                title: "View Outgoing Work Orders",
                description:
                    "Navigate to 'Work Order' > 'Outgoing' to see all work orders you've sent to MRO organizations, including WO ID, cost, expected completion date, and asset information.",
                image: "/media/help/outgoing-work-orders-list.png"
            },
            {
                title: "Review Work Order Details",
                description:
                    "Click on any work order to view comprehensive details including work order description, SLA penalty status, and associated asset information.",
                image: "/media/help/work-order-details-actions.png"
            },
            {
                title: "Monitor SLA Compliance",
                description:
                    "Check the SLA penalty status and click 'View SLA' to review the service level agreement terms that bind the service provider to complete the work order.",
                image: "/media/help/work-order-details-actions.png"
            },
            {
                title: "View Work Order Observations",
                description:
                    "Click 'View Observations' to see all progress reports and findings submitted by the MRO organization during work order execution.",
                image: "/media/help/work-order-details-actions.png"
            },
            {
                title: "Close Work Order",
                description:
                    "Once satisfied with the work performed and observations received, use 'Close/End WO' action to formally close the work order.",
                image: "/media/help/work-order-details-actions.png"
            }
        ]
    },
    {
        id: "mro-handle-incoming-work-orders",
        title: "MRO: Handle Incoming Work Orders",
        icon: <Settings size={20} />,
        difficulty: "Medium",
        category: "Work Order",
        steps: [
            {
                title: "View Incoming Work Orders",
                description:
                    "Navigate to 'Work Order' > 'Incoming' to see all work orders assigned to your organization from Asset Owners that need to be executed.",
                image: "/media/help/incoming-work-orders-list.png"
            },
            {
                title: "Review Work Order Details",
                description:
                    "Click on any incoming work order to view full details including work description, expected completion date, asset information, SLA requirements, and observation details with cost breakdown.",
                image: "/media/help/mro-accept-reject-work-order.png"
            },
            {
                title: "Accept or Reject Work Order",
                description:
                    "Use the 'Accept WO' action to confirm your organization will execute the work as specified, or 'Reject WO' if the work order cannot be completed as requested.",
                image: "/media/help/mro-accept-reject-work-order.png"
            },
            {
                title: "Assign to Staff or Execute Directly",
                description:
                    "Once accepted, you can either complete the work order yourself or use 'Assign to staff' to delegate it to appropriate technicians within your organization based on skills and availability.",
                image: "/media/help/assign-work-order-modal.png"
            },
            {
                title: "Track Work Progress and Observations",
                description:
                    "Monitor work progress through the observation details section, which shows service types, material providers, products, quantities, charges, and total costs for the work order.",
                image: "/media/help/work-order-observation-details.png"
            }
        ]
    },
    {
        id: "mro-assign-work-orders",
        title: "MRO: Assign Work Orders to Staff",
        icon: <UserPlus size={20} />,
        difficulty: "Medium",
        category: "Work Order",
        steps: [
            {
                title: "Access Incoming Work Orders",
                description:
                    "From your incoming work orders list, select the work order you want to assign to your staff members.",
                image: "/media/help/work-order-approval-workflow.png"
            },
            {
                title: "Review Work Requirements",
                description:
                    "Analyze the work order details, required skills, timeline, and asset location to determine the best staff member for assignment.",
                image: "/media/help/work-order-details-actions.png"
            },
            {
                title: "Select Staff Member",
                description:
                    "Choose the appropriate technician or team member based on their skills, current workload, availability, and proximity to the asset location.",
                image: "/media/help/assign-work-order-modal.png"
            },
            {
                title: "Set Assignment Details",
                description:
                    "Add assignment notes, specify priority level, provide additional instructions, and set expected completion timeline for the assigned staff member.",
                image: "/media/help/assign-work-order-modal.png"
            },
            {
                title: "Confirm Assignment",
                description:
                    "Review the assignment details and confirm. The assigned staff member will receive notifications and can view the work order in their assigned tasks.",
                image: "/media/help/assign-work-order-modal.png"
            }
        ]
    },
    {
        id: "technician-complete-work-orders",
        title: "Technician: Complete Assigned Work Orders",
        icon: <Play size={20} />,
        difficulty: "Medium",
        category: "Work Order",
        steps: [
            {
                title: "Access Assigned Work Orders",
                description:
                    "Navigate to 'Work Order' > 'Incoming' to view all work orders assigned to you by your MRO organization, or check 'My Work' for a combined view of everything awaiting your action.",
                image: "/media/help/work-order-details-actions.png"
            },
            {
                title: "Review Work Order Details",
                description:
                    "Click on the work order to view full details including work description, asset information, expected completion date, and any special instructions.",
                image: "/media/help/work-order-details-actions.png"
            },
            {
                title: "Start Work Execution",
                description:
                    "Begin the assigned work and update the work order status to 'In Progress' to notify your MRO organization that work has commenced.",
                image: "/media/help/work-order-details-actions.png"
            },
            {
                title: "Document Work Progress",
                description:
                    "Regularly update work progress, add notes about work performed, upload photos of completed work, and record any materials or parts used.",
                image: "/media/help/work-order-details-actions.png"
            },
            {
                title: "Submit Completion Report",
                description:
                    "When work is finished, mark the work order as complete, add final notes, upload completion photos, and submit for MRO review and forwarding to Asset Owner.",
                image: "/media/help/completed-work-order-details.png"
            }
        ]
    },
    {
        id: "view-completed-work-orders",
        title: "View Completed Work Orders",
        icon: <CheckCircle size={20} />,
        difficulty: "Easy",
        category: "Work Order",
        steps: [
            {
                title: "Access Completed Work Orders",
                description:
                    "Navigate to 'Work Order' > 'Completed' to view all work orders that have been finished and closed by both Asset Owners and MRO organizations.",
                image: "/media/help/completed-work-orders-list.png"
            },
            {
                title: "Review Completed Work Order Details",
                description:
                    "Click on any completed work order to view the final details including completion date, total cost, and all associated asset information.",
                image: "/media/help/completed-work-order-details.png"
            },
            {
                title: "Access Work Order Observations",
                description:
                    "Use 'View Observations' to review all progress reports, findings, and completion documentation submitted during the work order lifecycle.",
                image: "/media/help/completed-work-order-details.png"
            },
            {
                title: "Generate Work Order Certification",
                description:
                    "For quality assurance, use available actions like 'Work Order Certification' to generate completion certificates and 'Rate Service Provider' for performance evaluation.",
                image: "/media/help/completed-work-order-details.png"
            },
            {
                title: "Archive and Reference",
                description:
                    "Use completed work orders as reference for future similar work, cost estimation, timeline planning, and performance evaluation of service providers.",
                image: "/media/help/completed-work-orders-list.png"
            }
        ]
    },
    {
        id: "role-based-work-orders",
        title: "Understanding Role-Based Work Orders",
        icon: <Users size={20} />,
        difficulty: "Easy",
        category: "Work Order",
        steps: [
            {
                title: "Asset Owner Work Order Flow",
                description:
                    "Asset Owners create work orders and send them to MRO organizations. They can view 'Outgoing' work orders (sent to service providers), 'Approval Requests' (awaiting internal approval), and 'Completed' work orders (finished work).",
                image: "/media/help/work-order-navigation-menu.png"
            },
            {
                title: "MRO Organization Work Order Flow",
                description:
                    "MRO organizations receive work orders from Asset Owners. They see 'Incoming' work orders (new assignments), can assign work to staff, and track 'Completed' work orders (finished assignments).",
                image: "/media/help/work-order-approval-workflow.png"
            },
            {
                title: "Technician Work Order Flow",
                description:
                    "Technicians receive work orders assigned by their MRO organization. They can view 'Assigned' work orders, update progress, and mark work as complete for MRO review.",
                image: "/media/help/work-order-details-actions.png"
            },
            {
                title: "Approval Workflow",
                description:
                    "Work orders created by Asset Owners go through an approval process based on cost thresholds. Higher-value work orders require approval from senior personnel before being sent to MRO organizations.",
                image: "/media/help/work-order-approval-requests.png"
            },
            {
                title: "Flexible Assignment Options",
                description:
                    "If no responsible service provider is assigned to an asset/cluster, Asset Owners have flexibility to assign work orders to any available IMO, MRO, or External Technician organization.",
                image: "/media/help/create-work-order-form.png"
            }
        ]
    },
    {
        id: "work-order-approval-workflow",
        title: "Work Order Approval Workflow",
        icon: <ClipboardCheck size={20} />,
        difficulty: "Medium",
        category: "Work Order",
        steps: [
            {
                title: "Understanding Approval Triggers",
                description:
                    "When a work order is raised (from service request, reported fault, or directly), the system checks if there is an approval configuration and waits for required approvals before sending to MRO.",
                image: "/media/help/work-order-configuration.png"
            },
            {
                title: "Approval Level Hierarchy",
                description:
                    "Work orders are routed based on cost thresholds: Head O&M (up to 100,000 NGN), GM (up to 1,000,000 NGN), CEO (up to 10,000,000 NGN). Higher amounts require higher-level approval.",
                image: "/media/help/work-order-configuration.png"
            },
            {
                title: "Sequential Approval Process",
                description:
                    "Work orders move through the approval chain sequentially. Each approver in the hierarchy must approve before the work order advances to the next level or gets sent to the MRO.",
                image: "/media/help/work-order-approval-workflow.png"
            },
            {
                title: "Approval Status Tracking",
                description:
                    "Track approval status in the 'Approval Requests' section: 'Approved by me' (you've approved), 'Awaiting Approval' (waiting for your approval), showing clear workflow progress.",
                image: "/media/help/work-order-approval-requests.png"
            },
            {
                title: "Automatic MRO Assignment",
                description:
                    "Once all required approvals are obtained, the work order is automatically sent to the responsible MRO organization or the organization selected during work order creation.",
                image: "/media/help/work-order-approval-workflow.png"
            }
        ]
    },
    {
        id: "user-management",
        title: "User & Role Management",
        icon: <Users size={20} />,
        difficulty: "Medium",
        category: "Administration",
        steps: [
            {
                title: "Understanding Default User Roles",
                description:
                    "ANIKE comes with 5 pre-defined system roles that serve as organization admins:",
                details: [
                    {
                        role: "Asset Owner",
                        description:
                            "manages asset ownership and strategic decisions"
                    },
                    {
                        role: "MRO",
                        description:
                            "handles maintenance, repair, and operations coordination"
                    },
                    {
                        role: "Technician",
                        description: "performs hands-on maintenance and repairs"
                    },
                    {
                        role: "Super Admin",
                        description: "has full system access and oversight"
                    },
                    {
                        role: "Supplier",
                        description:
                            "manages vendor relationships and procurement"
                    }
                ],
                note:
                    "Each of these roles can create and manage custom roles within their organization.",
                image: "/media/help/default-user-roles.png"
            },
            {
                title: "Create Custom Roles",
                description:
                    "Navigate to your dashboard and click 'Create Role'. Enter a role name and select specific permissions from categories like Service Request Permissions (View, Create, Accept, Assign, Report Observations) and Work Order Permissions. Each permission controls what users can do in the system. Click 'Submit' to save the new role.",
                image: "/media/help/create-new-role.png"
            },
            {
                title: "Add Users to Your Organization",
                description:
                    "Go to Administration > Users > 'Add User'. Enter the user's details and assign them one of your custom roles. The user will inherit all permissions associated with that role.",
                image: "/media/help/add-user-to-organization.png"
            },
            {
                title: "Manage Role Permissions",
                description:
                    "You can modify existing custom roles by editing their permissions. This includes Service Request permissions (viewing, creating, accepting requests, assigning to staff, reporting observations) and Work Order permissions (creating, viewing, managing work orders). Changes take effect immediately for all users with that role.",
                image: "/media/help/manage-role-permissions.png"
            },
            {
                title: "Organization Registration",
                description:
                    "New organizations can request access by visiting the registration portal. Once approved, the organization admin can start creating custom roles and adding existing ANIKE users as staff members to build their team structure.",
                link: {
                    text: "https://www.anike.ng/auth/registration",
                    url: "https://www.anike.ng/auth/registration"
                },
                image: "/media/help/organization-registration.png"
            }
        ]
    },
    {
        id: "add-new-asset",
        title: "Add New Asset",
        icon: <FileText size={20} />,
        difficulty: "Easy",
        category: "Asset Management",
        steps: [
            {
                title: "Navigate to Asset Creation",
                description:
                    "From the main dashboard, click on 'Create' button and select 'Asset' from the dropdown menu.",
                image: "/media/help/create-new-asset.png"
            },
            {
                title: "Fill Asset Information",
                description:
                    "Enter the asset details including Asset Name, Asset ID, Location ID, and Location Name. These fields help identify and locate your asset.",
                image: "/media/help/create-new-asset.png"
            },
            {
                title: "Add Asset Description and Condition",
                description:
                    "Provide a detailed Asset Description and select the current Asset Condition from the dropdown (New, Good, Fair, Poor, etc.).",
                image: "/media/help/create-new-asset.png"
            },
            {
                title: "Set Manufacturer/Supplier",
                description:
                    "Enter the Manufacturer or Supplier information and click 'Submit' to create the asset.",
                image: "/media/help/create-new-asset.png"
            }
        ]
    },
    {
        id: "bulk-upload-assets",
        title: "Bulk Upload Assets",
        icon: <Settings size={20} />,
        difficulty: "Medium",
        category: "Asset Management",
        steps: [
            {
                title: "Access Bulk Upload",
                description:
                    "From the Create menu, select 'Bulk upload Asset' to access the bulk upload interface.",
                image: "/media/help/bulk-upload-asset.png"
            },
            {
                title: "Download Template",
                description:
                    "Click 'Download template' to get the Excel template with the correct format and required columns for asset data.",
                image: "/media/help/bulk-upload-asset.png"
            },
            {
                title: "Prepare Your Excel File",
                description:
                    "Fill in the downloaded template with your asset data, ensuring all required fields are completed according to the format.",
                image: "/media/help/bulk-upload-asset.png"
            },
            {
                title: "Upload Excel File",
                description:
                    "Drag and drop your completed Excel file into the upload area or click to browse and select the file, then click 'Upload'.",
                image: "/media/help/bulk-upload-asset.png"
            }
        ]
    },
    {
        id: "add-cluster",
        title: "Add New Cluster",
        icon: <Users size={20} />,
        difficulty: "Medium",
        category: "Asset Management",
        steps: [
            {
                title: "Navigate to Cluster Creation",
                description:
                    "From the Create menu, select 'Cluster' to start creating a new asset cluster.",
                image: "/media/help/create-cluster.png"
            },
            {
                title: "Enter Cluster Information",
                description:
                    "Fill in the Cluster Number and select the Cluster Condition from the dropdown menu.",
                image: "/media/help/create-cluster.png"
            },
            {
                title: "Set Location and Assets",
                description:
                    "Select the State and choose which assets to add to this cluster from the 'Select Assets to add to Cluster' dropdown.",
                image: "/media/help/create-cluster.png"
            },
            {
                title: "Assign Service Provider",
                description:
                    "Select the Responsible Service Provider who will handle maintenance for this cluster and set the OpEx to Date if applicable.",
                image: "/media/help/create-cluster.png"
            },
            {
                title: "Submit Cluster",
                description:
                    "Review all information and click 'Submit' to create the cluster. The selected assets will be grouped together.",
                image: "/media/help/create-cluster.png"
            }
        ]
    },
    {
        id: "view-cluster",
        title: "View Cluster Details",
        icon: <CheckCircle size={20} />,
        difficulty: "Easy",
        category: "Asset Management",
        steps: [
            {
                title: "Navigate to Cluster Management",
                description:
                    "Navigate to Operations > Sites / Clusters to see all clusters.",
                image: "/media/help/asset-navigation-menu.png"
            },
            {
                title: "Select Cluster to View",
                description:
                    "Click on any cluster from the list to view its detailed information and current status.",
                image: "/media/help/manage-cluster.png"
            },
            {
                title: "Review Cluster Information",
                description:
                    "View cluster details including Cluster Number, State, Responsible Service Provider, OpEx to Date, and associated assets.",
                image: "/media/help/manage-cluster.png"
            },
            {
                title: "Access Cluster Assets",
                description:
                    "Click 'View Asset' to see all assets assigned to this cluster and their current status.",
                image: "/media/help/manage-cluster.png"
            }
        ]
    },
    {
        id: "manage-cluster",
        title: "Manage Cluster",
        icon: <Settings size={20} />,
        difficulty: "Medium",
        category: "Asset Management",
        steps: [
            {
                title: "Access Cluster Actions",
                description:
                    "In the cluster details page, click the 'Actions' dropdown to see available management options.",
                image: "/media/help/manage-cluster.png"
            },
            {
                title: "Edit Cluster Information",
                description:
                    "Select 'Edit' from the actions menu to modify cluster details, change responsible service provider, or update cluster condition.",
                image: "/media/help/manage-cluster.png"
            },
            {
                title: "Raise Service Request",
                description:
                    "Click 'Raise SR' to create a service request directly for this cluster, which will apply to all assets in the cluster.",
                image: "/media/help/manage-cluster.png"
            },
            {
                title: "Create Work Order",
                description:
                    "Select 'Raise WO' to create a work order for maintenance activities on the entire cluster.",
                image: "/media/help/manage-cluster.png"
            },
            {
                title: "Delete Cluster",
                description:
                    "Use the 'Delete' option to remove the cluster (this will unassign assets but not delete them).",
                image: "/media/help/manage-cluster.png"
            }
        ]
    },
    {
        id: "manage-asset",
        title: "Manage Individual Assets",
        icon: <FileText size={20} />,
        difficulty: "Medium",
        category: "Asset Management",
        steps: [
            {
                title: "Navigate to Asset Management",
                description:
                    "Navigate to Operations > Assets > 'View Assets' to access asset management.",
                image: "/media/help/asset-navigation-menu.png"
            },
            {
                title: "Select Asset to Manage",
                description:
                    "Find and click on the asset you want to manage to view its detailed information page.",
                image: "/media/help/manage-asset.png"
            },
            {
                title: "Review Asset Details",
                description:
                    "View comprehensive asset information including Asset Name, ID, Location, Description, Condition, Address, State, LGA, and current cluster assignment.",
                image: "/media/help/manage-asset.png"
            },
            {
                title: "Edit Asset Information",
                description:
                    "Click 'Edit' in the Actions menu to modify asset details, update condition, or change location information.",
                image: "/media/help/manage-asset.png"
            },
            {
                title: "Assign Service Provider",
                description:
                    "Update the Responsible Service Provider field to assign maintenance responsibility to a specific service provider.",
                image: "/media/help/manage-asset.png"
            },
            {
                title: "Create Service Requests or Work Orders",
                description:
                    "Use 'Raise SR' or 'Raise WO' actions to create service requests or work orders directly for this specific asset.",
                image: "/media/help/manage-asset.png"
            }
        ]
    },
    {
        id: "assign-assets-to-cluster",
        title: "Assign Assets to Cluster",
        icon: <UserPlus size={20} />,
        difficulty: "Medium",
        category: "Asset Management",
        steps: [
            {
                title: "View Unassigned Assets",
                description:
                    "Navigate to the unassigned assets list to see all assets that are not currently assigned to any cluster.",
                image: "/media/help/view-asset-unassigned-to-cluster.png"
            },
            {
                title: "Filter and Search Assets",
                description:
                    "Use the search functionality to find specific assets by Asset Name, Asset ID, Location, or other criteria.",
                image: "/media/help/view-asset-unassigned-to-cluster.png"
            },
            {
                title: "Select Assets for Assignment",
                description:
                    "Choose the assets you want to assign to a cluster by selecting them from the unassigned assets list.",
                image: "/media/help/view-asset-unassigned-to-cluster.png"
            },
            {
                title: "Create New Cluster or Add to Existing",
                description:
                    "Either create a new cluster for the selected assets or add them to an existing cluster through the Actions menu.",
                image: "/media/help/view-asset-unassigned-to-cluster.png"
            },
            {
                title: "Confirm Assignment",
                description:
                    "Review the assignment details and confirm to move the assets from unassigned status to the selected cluster.",
                image: "/media/help/view-asset-unassigned-to-cluster.png"
            }
        ]
    },
    {
        id: "mro-manage-assigned-assets",
        title: "MRO: Manage Assigned Assets",
        icon: <FileText size={20} />,
        difficulty: "Medium",
        category: "Asset Management",
        steps: [
            {
                title: "Access Assigned Assets",
                description:
                    "Navigate to 'My Work' from the sidebar to view work assigned specifically to your organization for maintenance and management.",
                image: "/media/help/asset-navigation-menu-mro.png"
            },
            {
                title: "View Asset and Cluster Tabs",
                description:
                    "Use the 'Asset' tab to see individual assets assigned to your organization, and the 'Cluster' tab to view asset clusters under your management responsibility.",
                image: "/media/help/assigned-asset-view.png"
            },
            {
                title: "Review Asset Details",
                description:
                    "Click on any assigned asset to view comprehensive details including Asset Name, ID, Location, Condition, and current assignment status.",
                image: "/media/help/mro-asset-details.png"
            },
            {
                title: "Perform Asset Actions",
                description:
                    "Use the Actions dropdown to 'Report Fault' for issues or 'Assign Asset' to delegate specific assets to technicians within your organization.",
                image: "/media/help/mro-asset-details.png"
            },
            {
                title: "Search and Filter Assets",
                description:
                    "Use the search functionality to quickly find specific assets by name, ID, location, or condition to efficiently manage your assigned portfolio.",
                image: "/media/help/assigned-asset-view.png"
            }
        ]
    },
    {
        id: "mro-manage-organization-assets",
        title: "MRO: Manage Organization Assets",
        icon: <Users size={20} />,
        difficulty: "Medium",
        category: "Asset Management",
        steps: [
            {
                title: "Access Organization Assets",
                description:
                    "Navigate to Operations > Assets and Sites / Clusters to view all assets and clusters that belong to your organization's portfolio.",
                image: "/media/help/asset-navigation-menu-mro.png"
            },
            {
                title: "View Organization Clusters",
                description:
                    "The Cluster tab shows all clusters managed by your organization, including cluster numbers, conditions, states, and available actions.",
                image: "/media/help/organization-asset-cluster.png"
            },
            {
                title: "Access Cluster Details",
                description:
                    "Click on any cluster to view detailed information including Service Provider, Responsible Technician, and associated assets.",
                image: "/media/help/mro-cluster-details.png"
            },
            {
                title: "Manage Cluster Assignments",
                description:
                    "Use 'Assign Cluster' action to delegate cluster management to specific technicians or teams within your organization.",
                image: "/media/help/mro-cluster-details.png"
            },
            {
                title: "Monitor Asset Portfolio",
                description:
                    "Track the overall health and status of your organization's asset portfolio through the comprehensive cluster and asset views.",
                image: "/media/help/organization-asset-cluster.png"
            }
        ]
    },
    {
        id: "technician-view-assigned-work",
        title: "Technician: View Assigned Assets/Clusters",
        icon: <Settings size={20} />,
        difficulty: "Easy",
        category: "Asset Management",
        steps: [
            {
                title: "Access Your Assignments",
                description:
                    "Navigate to 'My Work' from the sidebar to view assets and clusters specifically assigned to you by your organization's MRO.",
                image: "/media/help/asset-navigation-menu-mro.png"
            },
            {
                title: "View Assigned Assets",
                description:
                    "The Asset tab shows individual assets assigned to you for maintenance, including asset details, location, and current condition.",
                image: "/media/help/assigned-asset-view.png"
            },
            {
                title: "View Assigned Clusters",
                description:
                    "The Cluster tab displays asset clusters under your responsibility, showing cluster numbers, conditions, and locations.",
                image: "/media/help/assigned-asset-view.png"
            },
            {
                title: "Report Issues",
                description:
                    "Use the 'Report Fault' action on any assigned asset or cluster to immediately notify your organization of maintenance issues or problems.",
                image: "/media/help/mro-asset-details.png"
            },
            {
                title: "Track Your Workload",
                description:
                    "Monitor all assets and clusters assigned to you to effectively plan and prioritize your maintenance activities and schedules.",
                image: "/media/help/assigned-asset-view.png"
            }
        ]
    },
    {
        id: "role-based-asset-access",
        title: "Understanding Role-Based Asset Access",
        icon: <Shield size={20} />,
        difficulty: "Easy",
        category: "Asset Management",
        steps: [
            {
                title: "Asset Owner Perspective",
                description:
                    "Asset Owners have full access to create, manage, and assign all assets and clusters. They can view the complete asset portfolio and make strategic decisions about asset allocation.",
                image: "/media/help/manage-asset.png"
            },
            {
                title: "MRO Organization View",
                description:
                    "MROs see assets assigned to their organization for management. They can view 'Assigned Assets' (delegated to them) and 'Organization Assets' (owned by their organization).",
                image: "/media/help/organization-asset-cluster.png"
            },
            {
                title: "Technician Assignment View",
                description:
                    "Technicians only see assets and clusters specifically assigned to them by their MRO organization. This focused view helps them concentrate on their direct responsibilities.",
                image: "/media/help/assigned-asset-view.png"
            },
            {
                title: "Navigation Differences",
                description:
                    "'My Work' and the Asset/Site pages show different things based on your role: Asset Owners see all management options under Operations, MROs see organizational views, and Technicians primarily use 'My Work' to see their own assignments.",
                image: "/media/help/asset-navigation-menu-mro.png"
            },
            {
                title: "Action Permissions",
                description:
                    "Available actions (Report Fault, Assign Asset, Assign Cluster) vary by role and organizational hierarchy, ensuring proper workflow and responsibility management.",
                image: "/media/help/mro-cluster-details.png"
            }
        ]
    },
    {
        id: "create-sla",
        title: "Create Service Level Agreement",
        icon: <></>,
        difficulty: "Medium",
        category: "SLA Management",
        steps: [
            {
                title: "Navigate to SLA Management",
                description:
                    "Navigate to Administration > 'SLAs' and click 'Create SLA' to start creating a new Service Level Agreement.",
                image: "/media/help/manage-sla.png"
            },
            {
                title: "Enter SLA Title",
                description:
                    "Provide a clear and descriptive title for your SLA that identifies the service level agreement purpose and scope.",
                image: "/media/help/create-sla.png"
            },
            {
                title: "Define SLA Terms and Description",
                description:
                    "In the Description/Terms field, specify detailed performance standards, response times, resolution timeframes, quality metrics, and any penalties for non-compliance. Be specific about measurable criteria.",
                image: "/media/help/create-sla.png"
            },
            {
                title: "Upload Supporting Documents",
                description:
                    "Click 'Choose file' to upload any supporting documents such as detailed contracts, technical specifications, or compliance requirements that supplement the SLA terms.",
                image: "/media/help/create-sla.png"
            },
            {
                title: "Review and Submit",
                description:
                    "Review all SLA information for accuracy and completeness, then click 'Submit' to create the SLA. It will now be available for binding service providers to work orders.",
                image: "/media/help/create-sla.png"
            }
        ]
    },
    {
        id: "manage-sla",
        title: "Manage Service Level Agreements",
        icon: <Settings size={20} />,
        difficulty: "Easy",
        category: "SLA Management",
        steps: [
            {
                title: "Access SLA Management",
                description:
                    "Navigate to the SLA Management section to view all existing Service Level Agreements in your organization.",
                image: "/media/help/manage-sla.png"
            },
            {
                title: "View SLA List",
                description:
                    "Review the list of all SLAs showing SLA Title, Date Created, Created By, and available Actions for each agreement.",
                image: "/media/help/manage-sla.png"
            },
            {
                title: "Search and Filter SLAs",
                description:
                    "Use the search functionality to find specific SLAs by title, creator, or creation date. This helps manage large numbers of agreements efficiently.",
                image: "/media/help/manage-sla.png"
            },
            {
                title: "Edit or Update SLAs",
                description:
                    "Click on any SLA from the list to view details, edit terms, update documentation, or modify performance criteria as business requirements change.",
                image: "/media/help/manage-sla.png"
            },
            {
                title: "Monitor SLA Usage",
                description:
                    "Track which SLAs are actively being used in work orders and monitor compliance rates to ensure service providers are meeting agreed standards.",
                image: "/media/help/manage-sla.png"
            }
        ]
    },
    {
        id: "mro-certify-work-orders",
        title: "Certify Completed Work Orders",
        icon: <CheckCircle size={20} />,
        difficulty: "Easy",
        category: "Work Order",
        steps: [
            {
                title: "Access Completed Work Orders",
                description:
                    "Navigate to 'Work Order' > 'Completed' to view all work orders that have been finished and are ready for certification.",
                image: "/media/help/mro-work-order-navigation.png"
            },
            {
                title: "Review Completed Work Details",
                description:
                    "Click on any completed work order to review the work performed, completion date, total costs, and all observation details before certification.",
                image: "/media/help/work-order-observation-details.png"
            },
            {
                title: "Initiate Work Order Certification",
                description:
                    "Click 'Work Order Certification' action to start the certification process, which acknowledges that the service provider has met all requirements for execution of the work order.",
                image: "/media/help/certify-work-order-modal.png"
            },
            {
                title: "Confirm Certification",
                description:
                    "Review the certification statement and click 'Certify' to formally acknowledge that all work order requirements have been met and the work is completed to standard.",
                image: "/media/help/certify-work-order-modal.png"
            },
            {
                title: "View Certified Work Orders",
                description:
                    "Access 'Work Order' > 'Certified' to view all work orders that have been certified, showing WO ID, cost, completion date, asset, and certification status.",
                image: "/media/help/certified-work-orders-list.png"
            }
        ]
    },
    {
        id: "asset-owner-certify-work-orders",
        title: "Asset Owner: Certify Completed Work Orders",
        icon: <CheckCircle size={20} />,
        difficulty: "Easy",
        category: "Work Order",
        steps: [
            {
                title: "Access Completed Work Orders",
                description:
                    "Navigate to 'Work Order' > 'Completed' to view all work orders that have been finished by MRO organizations and are ready for your certification.",
                image: "/media/help/completed-work-orders-list.png"
            },
            {
                title: "Review Completed Work Details",
                description:
                    "Click on any completed work order to review the work performed, completion date, total costs, and all observation details to ensure work meets your requirements.",
                image: "/media/help/asset-owner-certify-work-order.png"
            },
            {
                title: "Initiate Work Order Certification",
                description:
                    "Click 'Work Order Certification' action to start the certification process, which formally acknowledges that the service provider has met all requirements for execution of the work order.",
                image: "/media/help/asset-owner-certify-work-order.png"
            },
            {
                title: "Confirm Certification",
                description:
                    "Review the certification statement and click 'Certify' to formally acknowledge that the service provider has met all requirements and the work order execution is satisfactory.",
                image: "/media/help/asset-owner-certify-work-order.png"
            },
            {
                title: "Rate Service Provider Performance",
                description:
                    "After certification, use 'Rate Service Provider' to evaluate the MRO's performance and 'View Bill' to review final costs and billing details for the completed work order.",
                image: "/media/help/asset-owner-certify-work-order.png"
            }
        ]
    },
    // Pricebook & Warehouse Guides
    {
        id: "create-pricebook",
        title: "Create and Manage Pricebooks",
        icon: <DollarSign size={20} />,
        difficulty: "Easy",
        category: "Pricebook & Warehouse",
        steps: [
            {
                title: "Access Pricebook Management",
                description:
                    "Navigate to Administration > 'Pricebook' to access the pricebook management interface where you can create and manage pricing catalogs for your organization.",
                image: "/media/help/pricebook-overview.png"
            },
            {
                title: "Create New Pricebook",
                description:
                    "Click 'Create Pricebook' button to start creating a new pricebook catalog. This will open the pricebook creation form.",
                image: "/media/help/pricebook-overview.png"
            },
            {
                title: "Enter Pricebook Details",
                description:
                    "Fill in the Pricebook Name (e.g., 'Mechanical Work Pricebook', 'Street Lighting Pricebook') and provide a detailed Description explaining what types of items this pricebook contains.",
                image: "/media/help/add-pricebook-modal.png"
            },
            {
                title: "Submit Pricebook",
                description:
                    "Review the pricebook information and click 'Create' to establish the new pricebook. It will now appear in your pricebook list and be ready for adding items.",
                image: "/media/help/add-pricebook-modal.png"
            },
            {
                title: "Manage Existing Pricebooks",
                description:
                    "From the pricebook overview, you can view all your organization's pricebooks, edit their details using the edit icon, or access their contents using the view icon.",
                image: "/media/help/pricebook-overview.png"
            }
        ]
    },
    {
        id: "add-pricebook-items",
        title: "Add Items to Pricebook",
        icon: <Package size={20} />,
        difficulty: "Easy",
        category: "Pricebook & Warehouse",
        steps: [
            {
                title: "Access Pricebook Items",
                description:
                    "Click on a pricebook from the overview or use the view icon to access the pricebook's item list where you can see all current items and their pricing information.",
                image: "/media/help/pricebook-items-list.png"
            },
            {
                title: "Add Individual Item",
                description:
                    "Click 'Actions' dropdown and select 'Add Price' to add a single item to the pricebook. This opens a form for entering item details.",
                image: "/media/help/pricebook-items-list.png"
            },
            {
                title: "Enter Item Information",
                description:
                    "Fill in the item details including Name (e.g., 'Crank Battery'), Description (e.g., '12V 7AH Battery'), Brand, Model, and Price. Ensure all information is accurate for staff reference.",
                image: "/media/help/pricebook-items-list.png"
            },
            {
                title: "Save Item to Pricebook",
                description:
                    "Review all item information and save to add it to the pricebook. The item will now be available for staff to reference when creating observations or work orders.",
                image: "/media/help/pricebook-items-list.png"
            },
            {
                title: "Edit Existing Items",
                description:
                    "Use the edit icon next to any item to modify its details, update pricing, or correct information. This ensures your pricebook stays current and accurate.",
                image: "/media/help/pricebook-items-list.png"
            }
        ]
    },
    {
        id: "bulk-upload-pricebook",
        title: "Bulk Upload Pricebook Items",
        icon: <FileText size={20} />,
        difficulty: "Medium",
        category: "Pricebook & Warehouse",
        steps: [
            {
                title: "Access Bulk Upload",
                description:
                    "From the pricebook items list, click 'Actions' dropdown and select 'Bulk upload' to add multiple items to the pricebook at once using an Excel template.",
                image: "/media/help/pricebook-items-list.png"
            },
            {
                title: "Download Template",
                description:
                    "Download the Excel template provided by the system. This template contains the correct format and required columns for bulk uploading pricebook items.",
                image: "/media/help/pricebook-items-list.png"
            },
            {
                title: "Prepare Excel File",
                description:
                    "Fill in the downloaded template with your item data including Name, Description, Brand, Model, and Price columns. Ensure all required fields are completed and pricing is accurate.",
                image: "/media/help/pricebook-items-list.png"
            },
            {
                title: "Upload Excel File",
                description:
                    "Upload your completed Excel file using the bulk upload interface. The system will validate the data and import all items into your pricebook.",
                image: "/media/help/pricebook-items-list.png"
            },
            {
                title: "Verify Uploaded Items",
                description:
                    "After upload, review the pricebook to ensure all items were imported correctly with accurate pricing and complete information for staff reference.",
                image: "/media/help/pricebook-items-list.png"
            }
        ]
    },
    {
        id: "download-pricebook-data",
        title: "Download and Export Pricebook Data",
        icon: <FileText size={20} />,
        difficulty: "Easy",
        category: "Pricebook & Warehouse",
        steps: [
            {
                title: "Access Export Options",
                description:
                    "From the pricebook items list, click 'Actions' dropdown and select 'Download Material(s)' to export the current pricebook data for backup or sharing.",
                image: "/media/help/pricebook-items-list.png"
            },
            {
                title: "Select Export Format",
                description:
                    "Choose your preferred export format (typically Excel) and any specific data ranges or filters you want to apply to the export.",
                image: "/media/help/pricebook-items-list.png"
            },
            {
                title: "Download File",
                description:
                    "The system will generate and download the pricebook data file containing all item information including names, descriptions, brands, models, and current pricing.",
                image: "/media/help/pricebook-items-list.png"
            },
            {
                title: "Use Exported Data",
                description:
                    "Use the downloaded file for backup purposes, sharing with other team members, importing to other systems, or creating reports for management review.",
                image: "/media/help/pricebook-items-list.png"
            }
        ]
    },
    {
        id: "warehouse-inventory-management",
        title: "Warehouse Inventory Management",
        icon: <Warehouse size={20} />,
        difficulty: "Medium",
        category: "Pricebook & Warehouse",
        steps: [
            {
                title: "Access Warehouse Management",
                description:
                    "Navigate to Inventory > 'Warehouses' to access inventory management features where you can track stock levels and manage spare parts availability. The list page also shows a quick overview of total warehouses, materials tracked, stock value, and low-stock items.",
                image:
                    "/placeholder.svg?height=240&width=400&text=Warehouse Dashboard"
            },
            {
                title: "View Inventory Levels",
                description:
                    "Review current stock levels for all items in your warehouse, including quantities on hand, reorder levels, and items that may be running low.",
                image:
                    "/placeholder.svg?height=240&width=400&text=Inventory Levels"
            },
            {
                title: "Add New Inventory Items",
                description:
                    "Click 'Add Item' to register new spare parts or materials in your warehouse inventory, including item details, initial quantities, and storage locations.",
                image:
                    "/placeholder.svg?height=240&width=400&text=Add Inventory Item"
            },
            {
                title: "Update Stock Quantities",
                description:
                    "Record stock movements including receipts from suppliers, issues to work orders, and adjustments to maintain accurate inventory records.",
                image: "/placeholder.svg?height=240&width=400&text=Update Stock"
            },
            {
                title: "Set Reorder Levels",
                description:
                    "Configure minimum stock levels and reorder points for critical items to ensure adequate inventory is maintained for maintenance activities.",
                image:
                    "/placeholder.svg?height=240&width=400&text=Reorder Levels"
            },
            {
                title: "Generate Inventory Reports",
                description:
                    "Create reports showing stock levels, usage patterns, and reorder requirements to support procurement planning and inventory optimization.",
                image:
                    "/placeholder.svg?height=240&width=400&text=Inventory Reports"
            }
        ]
    },
    // Additional Warehouse Management Guides
    {
        id: "create-warehouse",
        title: "Create and Manage Warehouses",
        icon: <Warehouse size={20} />,
        difficulty: "Easy",
        category: "Pricebook & Warehouse",
        steps: [
            {
                title: "Access Warehouse Management",
                description:
                    "Navigate to Inventory > 'Warehouses' to access the warehouse management interface where you can create and manage multiple warehouse locations for your organization.",
                image: "/media/help/warehouse-overview.png"
            },
            {
                title: "Create New Warehouse",
                description:
                    "Click 'Add warehouse' button to start creating a new warehouse location. This will open the warehouse creation form where you can specify location details.",
                image: "/media/help/warehouse-overview.png"
            },
            {
                title: "Enter Warehouse Details",
                description:
                    "Fill in the Warehouse Name (e.g., 'LEDCo Warehouse', 'Main Storage Facility'), Warehouse Address for the physical location, and select the State and LGA from the dropdown menus.",
                image: "/media/help/add-warehouse-modal.png"
            },
            {
                title: "Submit Warehouse Information",
                description:
                    "Review all warehouse information for accuracy and click 'Create' to establish the new warehouse location. It will now appear in your warehouse list and be ready for inventory management.",
                image: "/media/help/add-warehouse-modal.png"
            },
            {
                title: "Manage Existing Warehouses",
                description:
                    "From the warehouse overview, you can view all your organization's warehouse locations, edit their details using the edit icon, or access their inventory using the view icon.",
                image: "/media/help/warehouse-overview.png"
            }
        ]
    },
    {
        id: "manage-warehouse-inventory",
        title: "Manage Warehouse Inventory",
        icon: <Package size={20} />,
        difficulty: "Medium",
        category: "Pricebook & Warehouse",
        steps: [
            {
                title: "Access Warehouse Inventory",
                description:
                    "Click on a warehouse from the overview or use the view icon to access the warehouse's inventory list where you can see all current stock items and their quantities.",
                image: "/media/help/warehouse-inventory-list.png"
            },
            {
                title: "View Inventory Details",
                description:
                    "Review the inventory table showing Material name, Description, Brand, Model, current Quantity on hand, Price per unit, and last updated date for comprehensive stock visibility.",
                image: "/media/help/warehouse-inventory-list.png"
            },
            {
                title: "Search and Filter Items",
                description:
                    "Use the search functionality to quickly find specific materials by name, description, brand, or model. This helps efficiently locate items in large inventories.",
                image: "/media/help/warehouse-inventory-list.png"
            },
            {
                title: "Monitor Stock Levels",
                description:
                    "Keep track of quantity levels for all materials and parts. Items with low quantities may need reordering to ensure adequate stock for maintenance activities.",
                image: "/media/help/warehouse-inventory-list.png"
            },
            {
                title: "Track Inventory Value",
                description:
                    "Monitor the total value of inventory by reviewing price information for each item, helping with asset valuation and budget planning for procurement.",
                image: "/media/help/warehouse-inventory-list.png"
            }
        ]
    },
    {
        id: "stock-in-materials",
        title: "Stock-In Materials and Parts",
        icon: <UserPlus size={20} />,
        difficulty: "Easy",
        category: "Pricebook & Warehouse",
        steps: [
            {
                title: "Access Stock-In Function",
                description:
                    "From the warehouse inventory list, click 'Actions' dropdown and select 'Stock-In +' to add new materials to inventory or increase quantities of existing items.",
                image: "/media/help/warehouse-inventory-list.png"
            },
            {
                title: "Select Material to Stock-In",
                description:
                    "Choose the material you want to add to inventory. You can either select an existing item to increase its quantity or add a completely new material to the warehouse.",
                image: "/media/help/warehouse-inventory-list.png"
            },
            {
                title: "Enter Stock-In Details",
                description:
                    "Specify the quantity being added, unit price if different from current price, supplier information, and any relevant notes about the stock receipt.",
                image: "/media/help/warehouse-inventory-list.png"
            },
            {
                title: "Record Stock-In Transaction",
                description:
                    "Confirm the stock-in details and save the transaction. The system will update the inventory quantity and record the transaction for audit trail purposes.",
                image: "/media/help/warehouse-inventory-list.png"
            },
            {
                title: "Verify Updated Inventory",
                description:
                    "After stock-in, verify that the inventory quantities have been updated correctly and the new stock levels are reflected in the warehouse inventory list.",
                image: "/media/help/warehouse-inventory-list.png"
            }
        ]
    },
    {
        id: "move-stock-out",
        title: "Move Stock Out for Work Orders",
        icon: <Settings size={20} />,
        difficulty: "Easy",
        category: "Pricebook & Warehouse",
        steps: [
            {
                title: "Access Stock Movement",
                description:
                    "From the warehouse inventory list, click 'Actions' dropdown and select 'Move Stock -' to record materials being issued for work orders or maintenance activities.",
                image: "/media/help/warehouse-inventory-list.png"
            },
            {
                title: "Select Items to Issue",
                description:
                    "Choose the materials and parts that need to be issued from inventory. You can select multiple items if they're being used for the same work order or project.",
                image: "/media/help/warehouse-inventory-list.png"
            },
            {
                title: "Specify Issue Details",
                description:
                    "Enter the quantity being issued, work order number or reference, technician receiving the materials, and purpose of the stock movement for proper tracking.",
                image: "/media/help/warehouse-inventory-list.png"
            },
            {
                title: "Record Stock Movement",
                description:
                    "Confirm the stock movement details and save the transaction. The system will reduce the inventory quantity and create a record of the material issue.",
                image: "/media/help/warehouse-inventory-list.png"
            },
            {
                title: "Track Material Usage",
                description:
                    "Monitor how materials are being used across different work orders and projects to optimize inventory levels and identify usage patterns.",
                image: "/media/help/warehouse-inventory-list.png"
            }
        ]
    },
    {
        id: "bulk-upload-warehouse",
        title: "Bulk Upload Warehouse Inventory",
        icon: <FileText size={20} />,
        difficulty: "Medium",
        category: "Pricebook & Warehouse",
        steps: [
            {
                title: "Access Bulk Upload",
                description:
                    "From the warehouse inventory list, click 'Actions' dropdown and select 'Bulk Upload' to add multiple inventory items at once using an Excel template.",
                image: "/media/help/warehouse-inventory-list.png"
            },
            {
                title: "Download Template",
                description:
                    "Download the Excel template provided by the system. This template contains the correct format and required columns for bulk uploading warehouse inventory items.",
                image: "/media/help/warehouse-inventory-list.png"
            },
            {
                title: "Prepare Inventory Data",
                description:
                    "Fill in the downloaded template with your inventory data including Material name, Description, Brand, Model, Quantity, and Price columns. Ensure all required fields are completed accurately.",
                image: "/media/help/warehouse-inventory-list.png"
            },
            {
                title: "Upload Excel File",
                description:
                    "Upload your completed Excel file using the bulk upload interface. The system will validate the data and import all inventory items into your warehouse.",
                image: "/media/help/warehouse-inventory-list.png"
            },
            {
                title: "Verify Uploaded Inventory",
                description:
                    "After upload, review the warehouse inventory to ensure all items were imported correctly with accurate quantities, pricing, and complete information.",
                image: "/media/help/warehouse-inventory-list.png"
            }
        ]
    }
];
